import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  formatMealDateTime,
  getMealPriorityLabel,
  getMealStatusLabel,
  getMealTypeLabel,
  toMealBoolean,
} from "../mealIncidenciasUtils";

const priorityColors = {
  HIGH: "error",
  MEDIUM: "warning",
  LOW: "info",
  INFO: "default",
};

const statusColors = {
  PENDIENTE: "error",
  EN_REVISION: "warning",
  VALIDADA: "success",
  DESCARTADA: "default",
};

export const MealPriorityChip = ({ value }) => (
  <Chip
    size="small"
    label={getMealPriorityLabel(value)}
    color={priorityColors[value] || "default"}
    variant={value === "HIGH" ? "filled" : "outlined"}
    sx={{ fontWeight: 800 }}
  />
);

export const MealStatusChip = ({ value }) => (
  <Chip
    size="small"
    label={getMealStatusLabel(value)}
    color={statusColors[value] || "default"}
    variant="outlined"
    sx={{ fontWeight: 800 }}
  />
);

export const MealConditionChip = ({ value }) => {
  const active = toMealBoolean(value);
  return (
    <Chip
      size="small"
      label={active ? "Activa" : "Ya no detectada"}
      color={active ? "error" : "success"}
      variant="outlined"
      sx={{ fontWeight: 800 }}
    />
  );
};

const EmptyRow = ({ message }) => (
  <TableRow>
    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
      <Typography color="text.secondary">{message}</Typography>
    </TableCell>
  </TableRow>
);

const MealIncidentTable = ({
  incidents,
  loading,
  error,
  emptyMessage,
  pagination,
  onPageChange,
  onPageSizeChange,
  onOpen,
  onRetry,
}) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      gap={1}
      p={2}
    >
      <Box>
        <Typography variant="h5" fontWeight={900}>Cola de incidencias</Typography>
        <Typography variant="body2" color="text.secondary">
          {pagination.total} incidencias según los filtros aplicados
        </Typography>
      </Box>
      {loading && <CircularProgress size={24} aria-label="Cargando incidencias" />}
    </Stack>

    {error && (
      <Alert severity="error" action={<Button color="inherit" onClick={onRetry}>Reintentar</Button>}>
        {error}
      </Alert>
    )}

    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="Cola de incidencias MEAL" sx={{ minWidth: 1040 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#004f7c" }}>
            {[
              "Prioridad",
              "Tipo",
              "Estado",
              "Condición",
              "Registros",
              "Responsable",
              "Detectada",
              "Actualizada",
              "Acción",
            ].map((label) => (
              <TableCell key={label} sx={{ color: "white", fontWeight: 900, whiteSpace: "nowrap" }}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && !error && incidents.length === 0 && <EmptyRow message={emptyMessage} />}
          {incidents.map((incident) => (
            <TableRow hover key={incident.id}>
              <TableCell><MealPriorityChip value={incident.priority} /></TableCell>
              <TableCell sx={{ minWidth: 190 }}>
                <Typography variant="body2" fontWeight={800}>{getMealTypeLabel(incident.type)}</Typography>
                <Typography variant="caption" color="text.secondary">#{incident.id}</Typography>
              </TableCell>
              <TableCell><MealStatusChip value={incident.status} /></TableCell>
              <TableCell><MealConditionChip value={incident.conditionActive} /></TableCell>
              <TableCell align="center">{Number(incident.affectedRecords) || 0}</TableCell>
              <TableCell>{incident.assignedToName || "Sin asignar"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{formatMealDateTime(incident.detectedAt)}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{formatMealDateTime(incident.updatedAt)}</TableCell>
              <TableCell>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={() => onOpen(incident)}
                  aria-label={`Revisar incidencia ${incident.id}`}
                >
                  Revisar
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      component="div"
      count={pagination.total}
      page={Math.max(0, pagination.page - 1)}
      rowsPerPage={pagination.pageSize}
      onPageChange={(event, value) => onPageChange(value + 1)}
      onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
      rowsPerPageOptions={[10, 20, 50]}
      labelRowsPerPage="Filas por página"
      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      disabled={loading}
    />
  </Paper>
);

MealIncidentTable.defaultProps = { incidents: [] };

export default MealIncidentTable;
